/**
 * Profile pictures („Profilbild“), one per person and optional (owner,
 * 05.10.2026: „Jeder soll die Möglichkeit haben, ein eigenes kleines
 * Profilbild zu hinterlegen … auf die sinnvolle Größe verkleinert“).
 *
 * The device crops to the centre square and encodes AVATAR_SIZE px JPEG; the
 * API checks the result with `inspectAvatar` — the same rule web, app and
 * server read here. Without a picture every client shows the initials on a
 * colour derived from the person's id, so every name has the same slot.
 */
import { inspectJpeg } from './jpeg';

/** Edge of the square the clients encode. Sharp at 48 pt on a 3x screen with room to spare. */
export const AVATAR_SIZE = 256;
/** JPEG quality the clients encode with. */
export const AVATAR_QUALITY = 0.8;
/** Upper bound the API accepts; a 256 px JPEG at 0.8 is 15–40 KB. */
export const AVATAR_MAX_BYTES = 100_000;
export const AVATAR_MIN_PX = 64;
export const AVATAR_MAX_PX = 512;

export type AvatarVerdict =
  | { readonly ok: true; readonly width: number; readonly height: number }
  | { readonly ok: false; readonly reason: 'not_jpeg' | 'exif' | 'too_large' | 'not_square' | 'size' };

/** SOF markers carry the frame size: C0–CF except DHT (C4), JPG (C8) and DAC (CC). */
function isSof(marker: number): boolean {
  return marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc;
}

/** Width and height from the first SOF segment, or null. */
export function jpegSize(bytes: Uint8Array): { width: number; height: number } | null {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return null;
  let i = 2;
  while (i + 3 < bytes.length) {
    if (bytes[i] !== 0xff) return null;
    while (i < bytes.length && bytes[i] === 0xff) i += 1;
    const marker = bytes[i]!;
    i += 1;
    // Standalone markers have no length.
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd9)) continue;
    if (i + 1 >= bytes.length) return null;
    const length = (bytes[i]! << 8) | bytes[i + 1]!;
    if (length < 2) return null;
    if (isSof(marker)) {
      if (i + 6 >= bytes.length) return null;
      const height = (bytes[i + 3]! << 8) | bytes[i + 4]!;
      const width = (bytes[i + 5]! << 8) | bytes[i + 6]!;
      return { width, height };
    }
    if (marker === 0xda) return null; // start of scan before any frame header
    i += length;
  }
  return null;
}

/** The server's rule for an uploaded picture: JPEG, no EXIF, ≤ 100 KB, square, 64–512 px. */
export function inspectAvatar(bytes: Uint8Array): AvatarVerdict {
  if (bytes.length > AVATAR_MAX_BYTES) return { ok: false, reason: 'too_large' };
  const jpeg = inspectJpeg(bytes);
  if (!jpeg.ok) return { ok: false, reason: jpeg.reason === 'exif' ? 'exif' : 'not_jpeg' };
  const size = jpegSize(bytes);
  if (!size) return { ok: false, reason: 'not_jpeg' };
  if (size.width !== size.height) return { ok: false, reason: 'not_square' };
  if (size.width < AVATAR_MIN_PX || size.width > AVATAR_MAX_PX) return { ok: false, reason: 'size' };
  return { ok: true, ...size };
}

/** The centre square of an image, for the client's crop before resizing. */
export function centreSquare(width: number, height: number): { x: number; y: number; side: number } {
  const side = Math.min(width, height);
  return { x: Math.floor((width - side) / 2), y: Math.floor((height - side) / 2), side };
}

/**
 * The section of a picture the person chose for their avatar, in image pixels:
 * the centre (`x`, `y`) of a square whose side is the picture's short side
 * divided by `zoom`. The web's crop dialog moves and zooms it; the app uses the
 * system's crop editor instead.
 */
export interface AvatarView {
  readonly zoom: number;
  readonly x: number;
  readonly y: number;
}

/** How far in the crop dialog lets the person zoom: a quarter of the short side is still 256 px from a 1024 px photo. */
export const AVATAR_MAX_ZOOM = 4;

type Size = { readonly width: number; readonly height: number };

/** The whole short side, centred: where the dialog starts. */
export function initialAvatarView(image: Size): AvatarView {
  return { zoom: 1, x: image.width / 2, y: image.height / 2 };
}

/** Zoom within 1…AVATAR_MAX_ZOOM, and the square kept inside the picture — no empty corner ever. */
export function clampAvatarView(image: Size, view: AvatarView): AvatarView {
  const zoom = Math.min(AVATAR_MAX_ZOOM, Math.max(1, Number.isFinite(view.zoom) ? view.zoom : 1));
  const half = Math.min(image.width, image.height) / zoom / 2;
  const clamp = (v: number, max: number) => Math.min(max - half, Math.max(half, Number.isFinite(v) ? v : max / 2));
  return { zoom, x: clamp(view.x, image.width), y: clamp(view.y, image.height) };
}

/** Dragging the picture by (dx, dy) screen pixels in a viewport of `viewport` pixels moves the section the other way. */
export function panAvatarView(image: Size, view: AvatarView, dx: number, dy: number, viewport: number): AvatarView {
  const perPixel = Math.min(image.width, image.height) / view.zoom / viewport;
  return clampAvatarView(image, { ...view, x: view.x - dx * perPixel, y: view.y - dy * perPixel });
}

/** The square to cut out, in whole image pixels (canvas `drawImage` source rectangle). */
export function avatarCropRect(image: Size, view: AvatarView): { x: number; y: number; side: number } {
  const v = clampAvatarView(image, view);
  const side = Math.min(image.width, image.height) / v.zoom;
  const x = Math.round(v.x - side / 2);
  const y = Math.round(v.y - side / 2);
  const s = Math.round(side);
  return { x: Math.min(Math.max(0, x), image.width - s), y: Math.min(Math.max(0, y), image.height - s), side: s };
}

/** Up to two letters: first and last word („Anna Beispiel“ → „AB“), one word → its first letter. */
export function initials(name: string): string {
  const words = name
    .trim()
    .split(/[\s\-–]+/)
    .filter((w) => /\p{L}/u.test(w));
  if (words.length === 0) return '?';
  const first = [...words[0]!].find((c) => /\p{L}/u.test(c)) ?? '?';
  if (words.length === 1) return first.toLocaleUpperCase('de-DE');
  const last = [...words[words.length - 1]!].find((c) => /\p{L}/u.test(c)) ?? '';
  return (first + last).toLocaleUpperCase('de-DE');
}

/** A hue 0–359 from the person's id: the same colour on every device, for the initials' circle. */
export function avatarHue(id: string): number {
  let h = 0x811c9dc5;
  for (const ch of id.toLowerCase()) {
    h ^= ch.codePointAt(0)!;
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h % 360;
}
